import { CheckCircle } from 'lucide-react';

interface ModuleCardProps {
  number: number;
  title: string;
  description: string;
  duration?: string;
  level?: 'Beginner' | 'Intermediate' | 'Advanced';
  isLocked?: boolean;
  isCompleted?: boolean;
  onStart?: () => void;
}

export default function ModuleCard({
  number,
  title,
  description,
  duration,
  level = 'Beginner',
  isLocked = false,
  isCompleted = false,
  onStart,
}: ModuleCardProps) {
  const levelColors = {
    Beginner: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Intermediate: 'bg-amber-50 text-amber-700 border-amber-200',
    Advanced: 'bg-rose-50 text-rose-700 border-rose-200',
  };

  return (
    <div
      className={`
        group relative bg-white rounded-2xl p-8
        border border-neutral-200
        transition-all duration-300 ease-out
        ${
          isLocked
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:shadow-xl hover:-translate-y-1 cursor-pointer'
        }
      `}
    >
      {/* Card glow effect on hover */}
      {!isLocked && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/0 via-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}

      {/* Content */}
      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-semibold text-sm">
                {number}
              </span>
              {level && !isLocked && !isCompleted && (
                <span
                  className={`px-3 py-1 text-xs font-medium rounded-full border ${levelColors[level]}`}
                >
                  {level}
                </span>
              )}
              {isCompleted && (
                <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Completed
                </span>
              )}
              {isLocked && (
                <span className="px-3 py-1 text-xs font-medium rounded-full border bg-neutral-100 text-neutral-600 border-neutral-200">
                  Coming Soon
                </span>
              )}
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">
              {title}
            </h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-neutral-600 text-sm leading-relaxed mb-6 line-clamp-2">
          {description}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
          {duration ? (
            <span className="text-xs text-neutral-500 font-medium">
              {duration}
            </span>
          ) : (
            <span className="text-xs text-neutral-400">Duration TBD</span>
          )}

          <button
            disabled={isLocked}
            onClick={onStart}
            className={`
              px-5 py-2.5 rounded-full text-sm font-semibold
              transition-all duration-200
              ${
                isLocked
                  ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                  : isCompleted
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-500/30 active:scale-95'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/30 active:scale-95'
              }
            `}
          >
            {isLocked ? 'Locked' : isCompleted ? 'Review' : 'Start Learning'}
          </button>
        </div>
      </div>

      {/* Locked overlay */}
      {isLocked && (
        <div className="absolute inset-0 rounded-2xl bg-white/40 backdrop-blur-[2px] pointer-events-none" />
      )}
    </div>
  );
}
